/**
 * Von Grundle Dialogue Engine
 * Handles the interactive consultation dialogue experience
 */

const VGDialogue = (function() {
  // Configuration
  const CONFIG = {
    optionsDelay: 300  // ms after text appears before showing options
  };

  // Dialogue content
  const DIALOGUE = {
    greetings: {
      guest: "Another seeker of arcane redress, I see. The rain always brings them. Come, come—don't drip on the grimoires.",
      loggedIn: "Ah... *[firstName]*. I've been expecting you. The tea leaves were... specific this morning.",
      returnVisit: "Ah... *[firstName]*. Back again? *[checks imaginary ledger]* You DO seem to accumulate grievances. Has the situation evolved, or is this a NEW offense?"
    },
    purposePrompt: "So then... what brings you to seek the counsel of Von Grundle this *[timeOfDay]*?",
    responses: {
      vengeance: "Ah, yes. The classic. *[settles into chair, steeples fingers]* Tell me of this... transgression.",
      gift: "Generosity? In THIS economy? *[genuinely surprised]* How... refreshing. Tell me of this fortunate recipient.",
      browse: "*[long, evaluating stare]*\n\nBrowsing. In MY emporium.\n\n*[even longer pause]*\n\n...Very well. But touch nothing that hums. And do NOT make eye contact with the thing in the corner.",
      uncertain: "*[understanding nod]*\n\nThen we shall divine your purpose together. I have been waiting since before your great-grandparents drew breath. I have... *[gestures expansively]* ...time."
    }
  };

  // State
  let state = {
    currentStage: 'greeting',
    collectedTags: [],
    dragonHintCount: 0,
    path: null
  };

  // DOM elements
  let elements = {};

  /**
   * Initialize the dialogue system
   */
  function init() {
    // Cache DOM elements
    elements = {
      dialogueText: document.getElementById('vg-dialogue-text'),
      optionsStage1: document.getElementById('vg-options-stage1'),
      responseArea: document.getElementById('vg-response-area'),
      responseText: document.getElementById('vg-response-text'),
      continueBtn: document.getElementById('vg-continue-btn')
    };

    if (!elements.dialogueText) return;

    // Load saved state from localStorage
    loadState();

    // Start with greeting
    showGreeting();
  }

  /**
   * Get time of day string
   */
  function getTimeOfDay() {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 17) return 'afternoon';
    if (hour >= 17 && hour < 21) return 'evening';
    return 'witching hour';
  }

  /**
   * Process text to replace placeholders
   */
  function processText(text) {
    const shopifyData = window.VGShopifyData || {};

    // Replace firstName
    if (shopifyData.customer && shopifyData.customer.firstName) {
      text = text.replace(/\*\[firstName\]\*/g, shopifyData.customer.firstName);
    }

    // Replace timeOfDay
    text = text.replace(/\*\[timeOfDay\]\*/g, getTimeOfDay());

    return text;
  }

  /**
   * Parse text for stage directions and format HTML
   */
  function formatDialogueText(text) {
    // Replace stage directions *[like this]* with styled spans
    text = text.replace(/\*\[([^\]]+)\]\*/g, '<span class="vg-stage-direction">*[$1]*</span>');

    // Replace emphasis (text in ALL CAPS that's 2+ chars)
    text = text.replace(/\b([A-Z]{2,})\b/g, '<span class="vg-emphasis">$1</span>');

    // Replace newlines with <br>
    text = text.replace(/\n/g, '<br>');

    return text;
  }

  /**
   * Display text immediately (no typewriter)
   */
  function showText(text, onComplete) {
    const processedText = processText(text);
    const formattedText = formatDialogueText(processedText);
    elements.dialogueText.innerHTML = formattedText;

    if (onComplete) {
      setTimeout(onComplete, CONFIG.optionsDelay);
    }
  }

  /**
   * Show greeting based on customer status
   */
  function showGreeting() {
    const shopifyData = window.VGShopifyData || {};
    let greeting;

    if (shopifyData.customer) {
      // Check if return visit
      const hasCompletedQuiz = localStorage.getItem('vg_quiz_completed') === 'true' || shopifyData.quizCompleted;

      if (hasCompletedQuiz) {
        greeting = DIALOGUE.greetings.returnVisit;
      } else {
        greeting = DIALOGUE.greetings.loggedIn;
      }
    } else {
      greeting = DIALOGUE.greetings.guest;
    }

    showText(greeting, function() {
      // After greeting, show the purpose prompt
      setTimeout(showPurposePrompt, 600);
    });
  }

  /**
   * Show the purpose prompt (Stage 1)
   */
  function showPurposePrompt() {
    showText(DIALOGUE.purposePrompt, function() {
      // Show options
      elements.optionsStage1.classList.add('visible');
      state.currentStage = 'stage1';
    });
  }

  /**
   * Handle option selection
   */
  function selectOption(optionId) {
    // Mark selected
    const options = elements.optionsStage1.querySelectorAll('.vg-option');
    options.forEach(opt => {
      if (opt.dataset.option === optionId) {
        opt.classList.add('selected');
      } else {
        opt.style.opacity = '0.5';
        opt.style.pointerEvents = 'none';
      }
    });

    // Record the choice
    state.path = optionId;
    state.collectedTags.push(optionId);
    saveState();

    // Get and display response
    const response = DIALOGUE.responses[optionId];
    const processedResponse = processText(response);
    const formattedResponse = formatDialogueText(processedResponse);

    // Show response area
    elements.responseArea.classList.add('visible');
    elements.responseText.innerHTML = formattedResponse;

    // Show continue button
    setTimeout(function() {
      elements.continueBtn.classList.add('visible');
    }, 300);
  }

  /**
   * Continue to next stage
   */
  function continueDialogue() {
    // For now, redirect based on path
    const routes = {
      vengeance: '/pages/quiz?path=vengeance',
      gift: '/pages/quiz?path=gift',
      browse: '/collections/all',
      uncertain: '/pages/quiz?path=discovery'
    };

    // Save state before navigating
    saveState();

    // Navigate to appropriate destination
    if (routes[state.path]) {
      window.location.href = routes[state.path];
    }
  }

  /**
   * Save state to localStorage
   */
  function saveState() {
    const saveData = {
      currentStage: state.currentStage,
      collectedTags: state.collectedTags,
      dragonHintCount: state.dragonHintCount,
      path: state.path,
      timestamp: Date.now()
    };
    localStorage.setItem('vg_dialogue_state', JSON.stringify(saveData));
  }

  /**
   * Load state from localStorage
   */
  function loadState() {
    try {
      const saved = localStorage.getItem('vg_dialogue_state');
      if (saved) {
        const data = JSON.parse(saved);
        // Only restore if less than 1 hour old
        if (Date.now() - data.timestamp < 3600000) {
          state.collectedTags = data.collectedTags || [];
          state.dragonHintCount = data.dragonHintCount || 0;
          state.path = data.path || null;
        }
      }
    } catch (e) {
      console.warn('Could not load VG dialogue state:', e);
    }
  }

  /**
   * Reset dialogue (for testing or restart)
   */
  function reset() {
    localStorage.removeItem('vg_dialogue_state');
    localStorage.removeItem('vg_quiz_completed');
    location.reload();
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API
  return {
    selectOption: selectOption,
    continueDialogue: continueDialogue,
    reset: reset,
    getState: function() { return state; }
  };
})();
